import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CITY_NAMES, WATER_SOURCES, CONCERNS, CALL_TIMES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  zip_code: "",
  city: "",
  water_source: "city",
  concern: "hard_water",
  best_time: "anytime",
};

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-3xl p-8 sm:p-10 text-slate-900 shadow-2xl" data-testid="lead-form-confirmation">
        <div className="w-12 h-12 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center text-xl font-bold">✓</div>
        <h3 className="text-2xl font-bold text-slate-900 mt-4">You're on the schedule list.</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Here's exactly what happens next:
        </p>
        <ol className="mt-4 space-y-3 text-sm text-slate-700">
          <li className="flex gap-3">
            <span className="font-bold text-sky-700 shrink-0">1.</span>
            Our local team will call or text you within one business day to confirm a convenient 30–45 minute window.
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-sky-700 shrink-0">2.</span>
            A Whitestone specialist tests your water right at your kitchen sink while you watch.
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-sky-700 shrink-0">3.</span>
            You get your readings explained in plain English — and options only if conditioning actually makes sense.
          </li>
        </ol>
        <p className="text-xs text-slate-500 mt-6 border-t border-slate-100 pt-4">
          No fee. No obligation. No pressure — that's the whole point.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-transparent bg-slate-50";
  const labelCls = "block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1";

  return (
    <div className="bg-white rounded-3xl p-8 sm:p-10 text-slate-900 shadow-2xl" data-testid="lead-form-card">
      <h3 className="text-2xl font-bold text-slate-900">Schedule Your Free Analysis</h3>
      <p className="text-sm text-slate-600 mt-1">
        Fill out the quick form below. Our local team will reach out to confirm your preferred time.
      </p>

      <form className="mt-8 space-y-5" onSubmit={submit} data-testid="lead-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="lead-name">Your Full Name</label>
            <input id="lead-name" data-testid="lead-name-input" type="text" required placeholder="Sarah Jenkins"
              value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="lead-phone">Phone Number</label>
            <input id="lead-phone" data-testid="lead-phone-input" type="tel" required placeholder="(405) 555-0192"
              value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="lead-email">Email Address</label>
            <input id="lead-email" data-testid="lead-email-input" type="email" required placeholder="sarah@example.com"
              value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="lead-zip">ZIP Code</label>
            <input id="lead-zip" data-testid="lead-zip-input" type="text" required placeholder="73013"
              value={form.zip_code} onChange={set("zip_code")} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="lead-city">City</label>
            <select id="lead-city" data-testid="lead-city-select" required value={form.city} onChange={set("city")}
              className={inputCls + " text-slate-800"}>
              <option value="" disabled>Select your city</option>
              {CITY_NAMES.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Other Oklahoma">Other Oklahoma community</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="lead-source">Water Source</label>
            <select id="lead-source" data-testid="lead-source-select" value={form.water_source} onChange={set("water_source")}
              className={inputCls + " text-slate-800"}>
              {WATER_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls} htmlFor="lead-concern">Biggest Water Concern</label>
          <select id="lead-concern" data-testid="lead-concern-select" value={form.concern} onChange={set("concern")}
            className={inputCls + " text-slate-800"}>
            {CONCERNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelCls} htmlFor="lead-best-time">What Is the Best Time to Call You?</label>
          <select id="lead-best-time" data-testid="lead-best-time-select" value={form.best_time} onChange={set("best_time")}
            className={inputCls + " text-slate-800"}>
            {CALL_TIMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div className="pt-2">
          <button type="submit" data-testid="lead-submit-button" disabled={submitting}
            className="w-full py-3.5 px-6 rounded-xl text-white bg-[#0f2b44] hover:bg-[#1971c2] transition shadow-lg text-center disabled:opacity-60 flex flex-col items-center justify-center gap-0.5">
            <span className="text-base font-bold leading-tight">{submitting ? "SENDING..." : "SHOW ME WHAT'S IN MY WATER"}</span>
            <span className="text-xs font-normal text-sky-200 leading-tight" data-testid="lead-submit-subtext">Submit</span>
          </button>
          <p className="text-center text-xs text-slate-500 mt-3">
            We respect your privacy. No spam, no high-pressure sales calls.
          </p>
        </div>
      </form>
    </div>
  );
}
